import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { Container, Grid, makeStyles } from '@material-ui/core';

import { propertyQuery, sessionQuery } from '@lib/state';
import { useObservable, Section, WebOnly, useDeviceType, ClientDeviceType } from '@lib/common';

import { reportQuery, reportService, exportRoomOccupancy } from 'app/state';
import { getDate, formatDateMidnight, DownloadButton, AuditLogs } from 'app/shared';
import { OccupancyHeader } from './occupancy-header';
import { OccupancyTable } from './occupancy-table';

const useStyles = makeStyles(() => ({
  downloadButtonContainer: {
    marginLeft: ({ isMobile }: { isMobile: boolean }) => (!isMobile ? 'auto' : 'unset'),
  },
}));

export const OccupancyReport: React.FC = () => {
  const device = useDeviceType();
  const isMobile = useMemo(() => device !== ClientDeviceType.Desktop, [device]);
  const { downloadButtonContainer } = useStyles({ isMobile });

  const [startDate, setStartDate] = useState(() => formatDateMidnight(startOfMonth(new Date())));
  const [endDate, setEndDate] = useState(() => formatDateMidnight(endOfMonth(new Date())));
  const [excludeNotCharged, setExcludeNotCharged] = useState(false);

  const propertyId = useObservable(sessionQuery.propertyId, 'async');
  const property = useObservable(propertyQuery.activeProperty);

  const occupancies = useObservable(reportQuery.roomOccupancy);

  const csvLink = useRef<any>(null);
  const csvFilename = `occupancy-report-from-${format(
    parseISO(startDate),
    'MM-dd-yyyy'
  )}-to-${format(parseISO(endDate), 'MM-dd-yyyy')}${
    excludeNotCharged ? '-ExcludesFreeRooms' : ''
  }.csv`;

  function exportCurrentProperty() {
    csvLink.current?.link.click();
  }

  useEffect(() => {
    if (propertyId && startDate && endDate) {
      reportService.loadRoomOccupancy(
        propertyId,
        getDate(startDate),
        getDate(endDate),
        excludeNotCharged
      );
    }
  }, [propertyId, startDate, endDate, excludeNotCharged]);

  if (!propertyId || !occupancies) return null;

  return (
    <Container maxWidth="xl">
      <OccupancyHeader
        startDate={startDate}
        endDate={endDate}
        excludeNotCharged={excludeNotCharged}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        setExcludeNotCharged={setExcludeNotCharged}
      >
        <WebOnly>
          <CSVLink
            ref={csvLink}
            filename={csvFilename}
            data={occupancies.map(exportRoomOccupancy)}
          />
          <Grid className={downloadButtonContainer} item>
            {/* Single action, no selector: the Combined Occupancy Report was removed from this
                menu while its range mode reported zeros for stay dates the daily housing
                snapshot never covered. The export now reads live data, but a range still
                summarizes only its first date; the action returns here when spec 005 FR-007
                restores it. */}
            <DownloadButton onDownload={exportCurrentProperty} />
          </Grid>
        </WebOnly>
      </OccupancyHeader>
      <Section title="Occupancy Report" textAlign="left" disableGutters>
        <OccupancyTable occupancies={occupancies} />
      </Section>
      <Section title="Audit Logs" textAlign="left" disableGutters>
        <AuditLogs scope="daily-occupancy-report" id={property?.customerId} />
      </Section>
    </Container>
  );
};
